import { useEffect, useState, useRef } from "react"
import { Link } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { getActiveYear } from "../utils/activeYear"
import { getRoundName } from "../utils/bracket"

interface Team {
  id: number
  name: string
  seed: number
  region: string
}

interface Game {
  id: number
  round: number
  region: string | null
  region_position: string | null
  bracket_position: number
  team1_id: number | null
  team2_id: number | null
  winner_id: number | null
  is_complete: boolean
}

export default function Results() {
  const [teams, setTeams] = useState<Team[]>([])
  const [games, setGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRound, setSelectedRound] = useState(1)
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [contestLive, setContestLive] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  
  // Ref for round selector buttons to auto-scroll
  const roundButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map())

  useEffect(() => { 
    loadData() 
  }, [])

  async function loadData() {
    setLoading(true)
    const year = await getActiveYear()
    setActiveYearState(year)

    // Check contest_live status
    const { data: yearData } = await supabase
      .from('years')
      .select('contest_live')
      .eq('year', year)
      .single()
    
    setContestLive(yearData?.contest_live ?? false)

    // Check if current user is admin
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single()
      setIsAdmin(profile?.is_admin || false)
    }

    const [teamsResult, gamesResult] = await Promise.all([
      supabase
        .from('tournament_teams')
        .select('id, seed, region, school:team_schools(name)')
        .eq('year', year),
      supabase.from('games').select('*').eq('year', year).order('bracket_position')
    ])

    // Flatten school name from join
    const teamsData = (teamsResult.data || []).map((t: any) => ({
      id: t.id,
      name: t.school?.name || 'Unknown',
      seed: t.seed,
      region: t.region,
    }))

    setTeams(teamsData)
    setGames(gamesResult.data || [])
    
    // Auto-select the current round:
    // 1. First check for an "in progress" round (some complete, some not)
    // 2. Otherwise find the first round with playable but unplayed games
    // 3. Default to the highest completed round or round 1
    const gamesData = gamesResult.data || []
    let currentRound = 1
    
    for (let r = 1; r <= 6; r++) {
      const roundGames = gamesData.filter((g: Game) => g.round === r)
      if (roundGames.length === 0) continue
      
      const completedCount = roundGames.filter((g: Game) => g.is_complete).length
      const playableCount = roundGames.filter((g: Game) => g.team1_id && g.team2_id).length
      const totalGames = roundGames.length
      
      // Round is "in progress" if some games are complete but not all playable games
      if (completedCount > 0 && completedCount < playableCount) {
        currentRound = r
        break
      }
      
      // Round is "upcoming" if no games complete but some are playable
      if (completedCount === 0 && playableCount > 0) {
        currentRound = r
        break
      }
      
      // If this round is fully complete, remember it and continue checking
      if (completedCount === totalGames && completedCount > 0) {
        currentRound = r
        // Keep going to see if next round has started
      }
    }
    
    setSelectedRound(currentRound)
    
    // Scroll the current round button into view after a short delay
    setTimeout(() => {
      const button = roundButtonRefs.current.get(currentRound)
      if (button) {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    }, 100)
    
    setLoading(false)
  }

  function getTeam(id: number | null): Team | undefined {
    return teams.find(t => t.id === id)
  }

  // Group games by round
  const gamesByRound = games.reduce((acc, game) => {
    if (!acc[game.round]) acc[game.round] = []
    acc[game.round].push(game)
    return acc
  }, {} as Record<number, Game[]>)

  const rounds = [1, 2, 3, 4, 5, 6]
  const currentRoundGames = gamesByRound[selectedRound] || []

  // Group current round games by region (for rounds 1-4)
  const gamesByRegion = currentRoundGames.reduce((acc, game) => {
    const key = game.region_position || 'finals'
    if (!acc[key]) acc[key] = []
    acc[key].push(game)
    return acc
  }, {} as Record<string, Game[]>)

  // Calculate overall progress
  const completedGames = games.filter(g => g.is_complete).length
  const totalGames = games.length

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Loading results...</div>
  }

  // Block access if contest is not live (admins can always view)
  if (!contestLive && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Contest Not Started</h2>
        <p className="text-gray-500 mb-6 max-w-md">
          Tournament results will be available once the contest goes live. 
          Check back after entries close!
        </p>
        <Link 
          to="/dashboard" 
          className="px-6 py-3 bg-[#1e3a5f] text-white rounded-lg font-medium hover:bg-[#002a6b]"
        >
          Back to Dashboard
        </Link>
      </div>
    )
  }

  if (games.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Tournament Results</h1>
          <p className="text-gray-500">Wright Picks {activeYear}</p>
        </div>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-8 text-center">
            <div className="text-amber-800 font-medium mb-2">No games yet</div>
            <p className="text-amber-600 text-sm">
              The tournament bracket hasn't been set up yet. Check back later!
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Tournament Results</h1>
        <p className="text-gray-500">Wright Picks {activeYear} • {completedGames} of {totalGames} games complete</p>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg p-3 shadow-sm">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">Tournament Progress</span>
          <span className="font-medium text-[#1e3a5f]">{Math.round((completedGames / totalGames) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-[#1e3a5f] to-[#F15A22] h-2 rounded-full transition-all"
            style={{ width: `${(completedGames / totalGames) * 100}%` }}
          />
        </div>
      </div>

      {/* Round Selector */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {rounds.map(round => {
          const roundGames = gamesByRound[round] || []
          const completedCount = roundGames.filter(g => g.is_complete).length
          const totalCount = roundGames.length
          const allComplete = totalCount > 0 && completedCount === totalCount
          const hasAnyComplete = completedCount > 0
          
          return (
            <button
              key={round}
              ref={(el) => { if (el) roundButtonRefs.current.set(round, el) }}
              onClick={() => setSelectedRound(round)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedRound === round
                  ? 'bg-[#1e3a5f] text-white'
                  : allComplete
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : hasAnyComplete
                      ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {getRoundName(round)}
              {totalCount > 0 && (
                <span className="ml-2 opacity-70">
                  {completedCount}/{totalCount}
                </span>
              )}
              {allComplete && selectedRound !== round && ' ✓'}
            </button>
          )
        })}
      </div>

      {/* Games Display */}
      {selectedRound <= 4 ? (
        // Rounds 1-4: Show by region
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(position => {
            const regionGames = gamesByRegion[position] || []
            const region = regionGames[0]?.region || position
            
            return (
              <Card key={position} className="bg-white border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-[#1e3a5f]">
                    {region}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {regionGames.length === 0 ? (
                    <div className="text-gray-400 text-sm py-4 text-center">No games in this round yet</div>
                  ) : (
                    regionGames.map(game => (
                      <GameResult
                        key={game.id}
                        game={game}
                        team1={getTeam(game.team1_id)}
                        team2={getTeam(game.team2_id)}
                      />
                    ))
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        // Rounds 5-6: Final Four and Championship
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">{getRoundName(selectedRound)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {currentRoundGames.length === 0 ? (
              <div className="text-gray-400 text-sm py-4 text-center">
                {selectedRound === 5 
                  ? "Complete the Elite 8 to see Final Four matchups"
                  : "Complete the Final Four to see the Championship"
                }
              </div>
            ) : (
              currentRoundGames.map(game => (
                <GameResult
                  key={game.id}
                  game={game}
                  team1={getTeam(game.team1_id)}
                  team2={getTeam(game.team2_id)}
                  large
                />
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// Game Result Component - Read-only display
function GameResult({ 
  game, 
  team1, 
  team2, 
  large = false
}: { 
  game: Game
  team1?: Team
  team2?: Team
  large?: boolean
}) {
  if (game.is_complete) {
    // Completed game - show winner/loser
    return (
      <div className={`rounded-lg bg-green-50 border border-green-200 ${large ? 'p-4' : 'p-3'}`}>
        <div className="space-y-1">
          <TeamResultRow 
            team={team1} 
            isWinner={game.winner_id === team1?.id}
            isCompleted={true}
            large={large}
          />
          <TeamResultRow 
            team={team2} 
            isWinner={game.winner_id === team2?.id}
            isCompleted={true}
            large={large}
          />
        </div>
      </div>
    )
  }

  if (team1 && team2) {
    // Upcoming game with both teams set - NOT completed, no winner/loser styling
    return (
      <div className={`rounded-lg bg-amber-50 border border-amber-200 ${large ? 'p-4' : 'p-3'}`}>
        <div className="space-y-1">
          <TeamResultRow team={team1} isCompleted={false} large={large} />
          <div className="text-center text-amber-600 text-xs font-medium">vs</div>
          <TeamResultRow team={team2} isCompleted={false} large={large} />
        </div>
      </div>
    )
  }

  // Game waiting for teams from previous round
  return (
    <div className={`rounded-lg bg-gray-50 border border-gray-100 ${large ? 'p-4' : 'p-3'}`}>
      <div className="space-y-1">
        <div className="text-gray-400 text-sm italic py-1">TBD</div>
        <div className="text-center text-gray-300 text-xs">vs</div>
        <div className="text-gray-400 text-sm italic py-1">TBD</div>
      </div>
    </div>
  )
}

// Team Result Row Component
function TeamResultRow({ 
  team, 
  isWinner = false,
  isCompleted = false,
  large = false 
}: { 
  team?: Team
  isWinner?: boolean
  isCompleted?: boolean
  large?: boolean
}) {
  if (!team) {
    return <div className="text-gray-400 text-sm italic py-1">TBD</div>
  }

  // Only show loser styling if the game is completed and this team didn't win
  const isLoser = isCompleted && !isWinner

  return (
    <div className={`flex items-center gap-2 ${isLoser ? 'opacity-50' : ''}`}>
      <span className={`${large ? 'w-7 h-7 text-sm' : 'w-5 h-5 text-xs'} ${
        isWinner ? 'bg-green-600' : 'bg-[#1e3a5f]'
      } text-white rounded-full flex items-center justify-center font-bold shrink-0`}>
        {team.seed}
      </span>
      <span className={`${large ? 'text-base' : 'text-sm'} ${
        isWinner ? 'font-bold text-green-700' : ''
      } ${isLoser ? 'line-through text-gray-400' : ''} flex-1`}>
        {team.name}
      </span>
      {isWinner && (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      )}
    </div>
  )
}

